import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, MapPin, Phone, CheckCircle2, XCircle, Cpu } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { useSentinel } from "@/context/SentinelContext";
import { formatTimestamp } from "@/data/mockData";

export default function Emergency() {
  const { contacts, incidents, latestIncident } = useSentinel();
  const inc = incidents.find((i) => i.level === "HIGH") || latestIncident;
  const [countdown, setCountdown] = useState(28);
  const [resolution, setResolution] = useState(null); // null | 'cancelled' | 'confirmed'

  useEffect(() => {
    if (resolution || countdown <= 0 || !inc) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown, resolution, inc]);

  const notified = [...contacts]
    .sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1))
    .slice(0, 3);

  if (!inc) {
    return (
      <DashboardLayout>
        <div
          className="flex min-h-[60vh] flex-col items-center justify-center rounded-lg border border-sentinel-border bg-sentinel-card p-10 text-center"
          data-testid="emergency-none-state"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" strokeWidth={1.6} />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-slate-100">No active incident</h1>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">
            There are no incidents in the Supabase feed yet. This screen activates when a high-risk
            incident is detected.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {resolution ? (
        <ResolvedState resolution={resolution} onReset={() => { setResolution(null); setCountdown(28); }} />
      ) : (
        <>
          {/* Alert banner */}
          <div
            className="overflow-hidden rounded-lg border border-red-500/50 bg-[#170d10]"
            data-testid="emergency-banner"
          >
            <div className="flex items-center gap-3 border-b border-red-500/30 bg-red-500/10 px-5 py-3">
              <span className="flex h-2.5 w-2.5 animate-pulse-ring rounded-full bg-red-500" />
              <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.22em] text-red-400">
                High Risk Detected
              </span>
              <span className="ml-auto font-mono text-[11px] text-red-400/70">{inc.id}</span>
            </div>

            <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr_1fr] lg:p-8">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Risk Score</p>
                <p className="mt-2 font-mono text-6xl font-bold leading-none text-red-400 sm:text-7xl">
                  {inc.score}<span className="text-2xl text-slate-600">/100</span>
                </p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-red-400">
                  {inc.rawLevel || "High"} · Live · Supabase
                </p>
                <p className="mt-4 text-lg font-medium text-slate-100">{inc.eventType}</p>
                <p className="mt-1 font-mono text-xs text-slate-500">{formatTimestamp(inc.timestamp)}</p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <StatusTile label="Verification" value={inc.verification === "—" ? "Awaiting response" : inc.verification} tone="warn" testid="emergency-verification-state" />
                  <StatusTile label="Escalation" value={(inc.escalation || "ACTIVE").toUpperCase()} tone="danger" testid="emergency-escalation-state" />
                </div>
              </div>

              {/* Countdown + actions */}
              <div className="flex flex-col justify-center rounded-lg border border-red-500/25 bg-black/20 p-6">
                <p className="text-center font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  Auto-escalation to contacts in
                </p>
                <p className="mt-2 text-center font-mono text-5xl font-bold tabular text-slate-100" data-testid="emergency-countdown">
                  0:{String(countdown).padStart(2, "0")}
                </p>
                <p className="mt-2 text-center text-sm text-slate-400">
                  If you are safe, cancel the alert. Otherwise confirm to dispatch
                  immediately.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <Button
                    onClick={() => setResolution("cancelled")}
                    data-testid="cancel-false-alarm-button"
                    className="h-12 w-full border border-sentinel-border bg-sentinel-card text-slate-100 hover:bg-[#1C2030]"
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Cancel — I'm safe
                  </Button>
                  <Button
                    onClick={() => setResolution("confirmed")}
                    data-testid="confirm-emergency-button"
                    className="h-12 w-full bg-red-600 text-white hover:bg-red-500"
                  >
                    <ShieldAlert className="mr-2 h-4 w-4" /> Confirm Emergency
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Context grid */}
          <div className="mt-4 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
            <div className="rounded-lg border border-sentinel-border bg-sentinel-card p-5 lg:col-span-2">
              <div className="flex items-center justify-between">
                <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Situation Context
                </h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                  {inc.source === "supabase" ? "Live record" : "Sentinel analysis"}
                </span>
              </div>
              <div className="mt-4 flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-sentinel-border bg-sentinel-accent/10">
                  <Cpu className="h-4 w-4 text-sentinel-accent" />
                </span>
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">AI Context</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-200">
                      {inc.aiContext || "No AI analysis is attached to this incident record yet."}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Recommended Action</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-200">
                      {inc.recommendedAction || "Verify the user's safety."}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-2 border-t border-sentinel-border pt-4 sm:grid-cols-2">
                {inc.sensors.map((s) => (
                  <div key={s.sensor} className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-slate-500">{s.sensor}</span>
                    <span className="text-right text-xs text-slate-300">{s.reading}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 md:space-y-5">
              <div className="rounded-lg border border-sentinel-border bg-sentinel-card p-5">
                <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Location</h2>
                {inc.location ? (
                  <div className="mt-3 flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-red-400" />
                    <div>
                      <p className="text-sm text-slate-100">{inc.location.label}</p>
                      <p className="mt-0.5 font-mono text-xs text-slate-400">{inc.location.coordinates}</p>
                      <p className="font-mono text-[11px] text-slate-500">Accuracy {inc.location.accuracy}</p>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-400">Location not attached to this incident record.</p>
                )}
              </div>

              <div className="rounded-lg border border-sentinel-border bg-sentinel-card p-5">
                <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Dispatch Queue
                </h2>
                <ul className="mt-3 space-y-3">
                  {notified.map((c, i) => (
                    <li key={c.id} className="flex items-center gap-3">
                      <Phone className="h-3.5 w-3.5 text-slate-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-100">{c.name}</p>
                        <p className="font-mono text-[11px] text-slate-500">{c.phone}</p>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-amber-400">
                        {i === 0 ? "Notifying" : "Queued"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function StatusTile({ label, value, tone, testid }) {
  const color = tone === "danger" ? "#EF4444" : tone === "warn" ? "#F59E0B" : "#94A3B8";
  return (
    <div className="rounded-md border border-sentinel-border bg-black/20 px-3 py-2.5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</p>
      <p className="mt-1 font-mono text-sm font-semibold" style={{ color }}>{value}</p>
    </div>
  );
}

function ResolvedState({ resolution, onReset }) {
  const confirmed = resolution === "confirmed";
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center rounded-lg border border-sentinel-border bg-sentinel-card p-10 text-center"
      data-testid="emergency-resolved-state"
    >
      <div
        className="flex h-20 w-20 items-center justify-center rounded-full border"
        style={{
          borderColor: confirmed ? "rgba(239,68,68,0.4)" : "rgba(16,185,129,0.4)",
          backgroundColor: confirmed ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
        }}
      >
        {confirmed ? (
          <ShieldAlert className="h-9 w-9 text-red-400" strokeWidth={1.6} />
        ) : (
          <CheckCircle2 className="h-9 w-9 text-emerald-400" strokeWidth={1.6} />
        )}
      </div>
      <h1 className="mt-5 font-heading text-2xl font-semibold text-slate-100">
        {confirmed ? "Emergency Confirmed" : "Alert Cancelled"}
      </h1>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">
        {confirmed
          ? "Emergency contacts have been dispatched with your location and situational context. Stay on the line if you are able."
          : "Marked as a false alarm. No contacts were alerted and the incident has been logged as resolved."}
      </p>
      <div className="mt-7 flex gap-3">
        <Button asChild variant="outline" className="border-sentinel-border bg-transparent text-slate-200 hover:bg-[#1C2030]">
          <Link to="/dashboard" data-testid="emergency-return-dashboard">Return to Dashboard</Link>
        </Button>
        <Button onClick={onReset} className="bg-sentinel-accent text-white hover:bg-sentinel-accenthover" data-testid="emergency-reset-demo">
          Reset demo
        </Button>
      </div>
    </div>
  );
}
